import { endOfDay, isAfter, isBefore, isValid, startOfWeek } from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { getStartHour, getTimeUtc } from "./dates"
import { getRefId, isMongoId } from "./utils"
import { isSectionAction } from "./sections"

/**
 * The prefix of a task code: "ABC-12" -> "ABC". A code with no dash is its own prefix.
 *
 * @param code {string}
 * @return {string}
 */
export function getTaskCodePrefix(code) {
    return String(code || "").split("-")[0]
}

/**
 * The next free code for a prefix: one past the highest number already used,
 * so deleted tasks and gaps never hand out a code that exists.
 *
 * @param prefix {string}
 * @param codes {string[]} every code in the project the task lands in
 * @return {string}
 */
export function nextTaskCode(prefix, codes = []) {
    const highest = (codes || []).reduce((max, code) => {
        if (typeof code !== "string" || !code.startsWith(prefix + "-")) return max
        const n = Number(code.slice(prefix.length + 1))
        return Number.isInteger(n) && n > max ? n : max
    }, 0)

    return prefix + "-" + (highest + 1)
}

/**
 * The copy's dates, kept inside the project it lands in: a task starting before
 * the project moves to its start (same length), one starting after it ends
 * becomes a one day task on the last day.
 *
 * @param ai {Object} the task being copied
 * @param mission {Object} the project it lands in (planStartDate, planEndDate)
 * @return {{week, startDate, startHour, endDate, estimatedDuration}} UTC times
 */
export function getDuplicateDates(ai, mission) {
    const toDate = (t) => (t || t === 0 ? toZonedTime(t) : undefined)
    const usable = (d) => d && isValid(d)

    const aiStart = toDate(ai.startDate)
    const aiWeek = toDate(ai.week)
    const projectStart = toDate(mission.planStartDate)
    const projectEnd = toDate(mission.planEndDate)

    let startDate = aiStart
    let week = aiWeek
    let endDate = toDate(ai.endDate)
    let estimatedDuration = ai.estimatedDuration
    let startHour = ai.startHour

    if (usable(aiStart) && usable(projectStart) && isBefore(aiStart, projectStart)) {
        startDate = projectStart
        startHour = getStartHour(startDate)
        if (usable(endDate)) {
            endDate = new Date(endDate.getTime() + (projectStart.getTime() - aiStart.getTime()))
        }
    }

    if (usable(aiWeek) && usable(projectStart)) {
        const firstWeek = startOfWeek(projectStart, { weekStartsOn: 1 })
        if (isBefore(aiWeek, firstWeek)) week = firstWeek
    }

    if (usable(aiStart) && usable(projectEnd) && isAfter(aiStart, projectEnd)) {
        startDate = projectEnd
        estimatedDuration = 8
        endDate = endOfDay(startDate)
        startHour = getStartHour(startDate)
    }

    return {
        week: usable(week) ? getTimeUtc(week) : null,
        startDate: usable(startDate) ? getTimeUtc(startDate) : null,
        startHour,
        endDate: usable(endDate) ? getTimeUtc(endDate) : null,
        estimatedDuration,
    }
}

/**
 * The new task's fields for a copy. Comments, approvals, sign-offs and history
 * are never copied. In the same project the copy is created under the task's own action, so
 * it lands in the same board section (a section is the task's actionId). Into another
 * project: back to To do, no section (the caller creates it under that project's
 * catch-all), project-only tags dropped, people kept only if they are on that project.
 *
 * @param ai {Object} the task being copied
 * @param mission {Object} the project it lands in
 * @param isOther {boolean} copying into another project
 * @param code {string} the copy's code (see nextTaskCode)
 * @return {Object}
 */
export function buildDuplicateTask({ ai, mission, isOther, code }) {
    const onProject = new Set((mission.people || []).map((p) => getRefId(p)).filter(Boolean))
    const keepPerson = (p) => !isOther || !onProject.size || onProject.has(getRefId(p))

    const checklist = (ai.checklist || []).map((cl) => ({
        title: cl.title,
        description: cl.description,
        sortIndex: cl.sortIndex,
        people: isOther ? [] : cl.people,
        col: cl.col,
        color: cl.color,
    }))

    return {
        ...getDuplicateDates(ai, mission),
        code,
        shortDescription: ai.shortDescription,
        people: (ai.people || []).filter(keepPerson),
        title: !isOther && ai.title ? ai.title + " (Copy)" : ai.title,
        description: ai.description,
        col: isOther ? "todo" : ai.col,
        y: ai.y || 0,
        sortIndex: ai.y || 0,
        // section ids left in tags from before sections were actions are not copied
        tags: (ai.tags || []).filter((t) => (isOther ? !isMongoId(t) : !isSectionAction(mission, t))),
        complexityMultiplier: ai.complexityMultiplier,
        checklist,
        billRate: ai.billRate,
        billUnit: ai.billUnit,
        percentComplete: ai.percentComplete,
        templateId: ai.templateId,
        // Stays within the project: another project may have no toggle to turn it back on
        ...(!isOther && ai.noTimeEntry ? { noTimeEntry: true } : {}),
    }
}
